/**
 * A screen's `constants.idLink`: how that screen turns the value in its
 * selection-identifier column into an in-app URL. Either a function, or a
 * prefix the value is appended to.
 */
export type IdLink = string | ((identifierValue: string) => string) | undefined;

/**
 * The URL a screen's `idLink` produces for one identifier value, or null when
 * the screen declares no link.
 *
 * The value must be the identifier exactly as the data carries it: these URLs
 * are keyed on real identifiers (`GSID-DEM-DS00580`, `DEMO-AD-2026-02-101-001`)
 * that do not survive `normalizeString`.
 *
 * Encoding happens here, once, before either branch, so a value containing `/`
 * or `?` lands in one path segment instead of forging new ones. No `idLink`
 * encodes its own argument, so this cannot double-encode; one that started to
 * would need to stop rather than have the encoding moved back out.
 */
export const resolveIdLink = (
  idLink: IdLink,
  identifierValue: string,
): string | null => {
  if (!idLink || !identifierValue) return null;
  const encoded = encodeURIComponent(identifierValue);
  return typeof idLink === "function"
    ? idLink(encoded)
    : `${idLink}/${encoded}`;
};
